/**
 * The UI context lives in its own module so that hot-reloading ui.tsx in
 * development can't leave the provider and its consumers on two different
 * context objects ("useUI must be used inside <UIProvider>").
 */
import { createContext, useContext } from 'react';
import type { DraftItem } from '../../shared/api';
import type { ISODate, Meal } from '../../shared/dates';
import type { LogTabInput } from './logTabs';

export type { LogTab, LogTabInput } from './logTabs';
export type LogMode = 'text' | 'voice' | 'photo' | 'label' | 'barcode';

export interface LogOptions {
  meal?: Meal;
  date?: ISODate;
  /** Which chip to open. `recipes` and `custom` are the old chips: both open My foods. */
  tab?: LogTabInput;
  mode?: LogMode;
  drafts?: DraftItem[];
  draftSource?: 'what_fits';
  /** A recipe to open at one serving on My foods, by its food ref (`recipe:<id>`). The sheet then opens there, on the list, whatever `tab` says. */
  recipe?: string;
}

export interface Toast {
  id: number;
  message: string;
  tone: 'default' | 'error';
  action?: { label: string; onClick: () => void };
}

export interface UIContextValue {
  today: ISODate;
  selectedDate: ISODate;
  setSelectedDate: (date: ISODate) => void;
  openLog: (opts?: LogOptions) => void;
  openWeighIn: (opts?: { date?: ISODate }) => void;
  openWhatFits: (opts?: { meal?: Meal; date?: ISODate }) => void;
  toast: (message: string, opts?: { tone?: 'default' | 'error'; action?: Toast['action'] }) => void;
}

export const UIContext = createContext<UIContextValue | null>(null);

export function useUI(): UIContextValue {
  const ctx = useContext(UIContext);
  if (!ctx) throw new Error('useUI must be used inside <UIProvider>');
  return ctx;
}
